import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Calendar, 
  CalendarCheck,
  MessageSquare, 
  Users, 
  Package,
  Shield,
  Settings,
  MessageCircle,
  X
} from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';
import { useAuth } from '../../hooks/useAuth';
import { ReactComponent as RodBotLogo } from '../../assets/images/rodbotsvg.svg';
import './Sidebar.css';

const Sidebar = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { user } = useAuth();

  const enabledFeatures = React.useMemo(() => {
    const raw = (user?.features || '').toString().trim();
    if (!raw) return null; // null => all enabled
    return new Set(
      raw
        .split(',')
        .map(s => s.trim().toLowerCase())
        .filter(Boolean)
    );
  }, [user]);

  const isFeatureEnabled = (featureKey) => {
    if (!enabledFeatures) return true;
    return enabledFeatures.has(featureKey);
  };

  // Chats + Leads first — the main selling surface of the product.
  const navItems = [
    {
      path: '/chats',
      icon: MessageSquare,
      label: t('nav.chats'),
      feature: 'chats',
    },
    {
      path: '/leads',
      icon: Users,
      label: t('nav.leads'),
      feature: 'leads',
    },
    {
      path: '/appointments',
      icon: Calendar,
      label: t('nav.appointments'),
      feature: 'appointments',
    },
    {
      path: '/booking-link',
      icon: CalendarCheck,
      label: t('nav.bookingLink', 'Appointments by external link'),
      feature: 'appointments',
    },
    {
      path: '/products',
      icon: Package,
      label: t('nav.products'),
      feature: 'products',
    },
    {
      path: '/phone-controls',
      icon: Shield,
      label: t('nav.phoneControls'),
      feature: 'phone_controls',
    },
    {
      path: '/settings',
      icon: Settings,
      label: t('nav.settings'),
      feature: 'settings',
    },
    {
      path: '/connect',
      icon: MessageCircle,
      label: t('nav.connectWhatsapp', 'Connect WhatsApp'),
      alwaysShow: true,
      utility: true,
    },
  ];

  // Escape closes the mobile drawer
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="sidebar-overlay"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={`sidebar ${isOpen ? 'sidebar-open' : ''} ${isRTL ? 'rtl' : ''}`}
        aria-label={t('nav.dashboard', 'Navigation')}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <RodBotLogo className="logo-icon" />
            <span className="logo-text">RodBotAI</span>
          </div>
          <button 
            className="sidebar-close"
            onClick={onClose}
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.filter(item => item.alwaysShow || isFeatureEnabled(item.feature)).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `nav-item ${item.utility ? 'nav-item-utility' : ''} ${
                  isActive ? 'nav-item-active' : ''
                }`
              }
              onClick={onClose}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <p className="sidebar-version">v1.0.0</p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

